// ============================================================================
// Lead Manager — real FastAPI + MongoDB backend (Phase 2 · Step 2)
// ----------------------------------------------------------------------------
// Source of truth = backend `/api/leads` (RBAC + client scope enforced server
// side; portal users receive a sanitized view of their own client's leads).
// A synchronous in-memory cache (hydrated once at auth bootstrap) keeps the
// existing LeadManager UI working unchanged. Status/notes edits are optimistic
// and persisted in the background, re-hydrating on error.
// ============================================================================

import { api } from "@/lib/api";
import { clientById } from "@/data/mockData";

// ---------------------------------------------------------------- constants
export const LEAD_STATUS = {
  new: { label: "Nuovo", tone: "info" },
  to_contact: { label: "Da contattare", tone: "warning" },
  contacted: { label: "Contattato", tone: "info" },
  called_1: { label: "Chiamato 1", tone: "info" },
  called_2: { label: "Chiamato 2", tone: "warning" },
  no_answer: { label: "Non risponde", tone: "warning" },
  appointment_booked: { label: "Agendato", tone: "ok" },
  appointment_cancelled: { label: "Appuntamento annullato", tone: "warning" },
  no_answer_1: { label: "Nessuna risposta 1", tone: "warning" },
  no_answer_2: { label: "Nessuna risposta 2", tone: "warning" },
  no_show: { label: "No show", tone: "error" },
  cancelled: { label: "Annullato", tone: "neutral" },
  not_interested: { label: "Non interessato", tone: "neutral" },
  wrong_number: { label: "Numero errato", tone: "neutral" },
  outside_service_area: { label: "Fuori zona", tone: "neutral" },
  program_sold: { label: "Programma venduto", tone: "ok" },
  converted: { label: "Convertito", tone: "ok" },
};

// Stile colorato per la colonna Stato (badge) + colore riga, per Portale e Cliente › Lead.
export const LEAD_STATUS_STYLE = {
  new: { label: "Da gestire", badge: "bg-muted text-muted-foreground border-border", row: "" },
  called_1: { label: "Chiamato 1", badge: "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-500/30", row: "" },
  called_2: { label: "Chiamato 2", badge: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30", row: "" },
  no_answer: { label: "Non risponde", badge: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30", row: "" },
  appointment_booked: { label: "Agendato", badge: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40", row: "bg-emerald-50 hover:bg-emerald-100/70 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/[0.15]" },
  appointment_cancelled: { label: "Appuntamento annullato", badge: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-500/30", row: "" },
  converted: { label: "Convertito", badge: "bg-green-100 text-green-700 border-green-300 dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/40", row: "bg-green-50/70 hover:bg-green-100/60 dark:bg-green-500/[0.08] dark:hover:bg-green-500/[0.12]" },
  outside_service_area: { label: "Fuori zona", badge: "bg-red-100 text-red-600 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30", row: "bg-red-50/60 hover:bg-red-100/50 dark:bg-red-500/[0.06] dark:hover:bg-red-500/[0.1]" },
  wrong_number: { label: "Numero errato", badge: "bg-red-100 text-red-600 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30", row: "bg-red-50/60 hover:bg-red-100/50 dark:bg-red-500/[0.06] dark:hover:bg-red-500/[0.1]" },
  not_interested: { label: "Non interessato", badge: "bg-rose-100 text-rose-600 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30", row: "bg-red-50/60 hover:bg-red-100/50 dark:bg-red-500/[0.06] dark:hover:bg-red-500/[0.1]" },
};
export const leadStatusStyle = (s) => LEAD_STATUS_STYLE[s] || { label: (LEAD_STATUS[s]?.label || s || "—"), badge: "bg-muted text-muted-foreground border-border", row: "" };

// Sfondo cella (senza hover) per il gruppo colonne di sinistra della tabella lead.
export const LEAD_ROW_BG = {
  appointment_booked: "bg-emerald-50/70 dark:bg-emerald-500/10",
  converted: "bg-green-50/70 dark:bg-green-500/[0.08]",
  outside_service_area: "bg-red-50/60 dark:bg-red-500/[0.07]",
  wrong_number: "bg-red-50/60 dark:bg-red-500/[0.07]",
  not_interested: "bg-red-50/60 dark:bg-red-500/[0.07]",
};
export const statusRowBg = (s) => LEAD_ROW_BG[s] || "";

// Stile etichette + celle per la colonna Consulenza (colora solo Consulenza + Valore vendita).
export const CONSULT_STYLE = {
  acquistato: { badge: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40", cell: "bg-emerald-50/70 dark:bg-emerald-500/10" },
  non_acquistato: { badge: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/40", cell: "bg-amber-50/70 dark:bg-amber-500/10" },
  app_rimandato: { badge: "bg-sky-100 text-sky-700 border-sky-300 dark:bg-sky-500/20 dark:text-sky-300 dark:border-sky-500/40", cell: "bg-sky-50/70 dark:bg-sky-500/10" },
  non_presentato: { badge: "bg-red-100 text-red-600 border-red-300 dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/40", cell: "bg-red-50/70 dark:bg-red-500/10" },
  non_trattabile: { badge: "bg-muted text-muted-foreground border-border", cell: "" },
};
export const consultStyle = (v) => CONSULT_STYLE[v] || { badge: "bg-muted text-muted-foreground border-border", cell: "" };

// Etichette che il cliente può impostare dal portale (set operativo completo).
export const PORTAL_LEAD_STATUSES = ["new", "called_1", "called_2", "no_answer", "appointment_booked", "appointment_cancelled", "outside_service_area", "wrong_number", "not_interested"];

// Esito consulenza (colonna "Consulenza") — modificabile dal cliente e dal team.
export const CONSULTATION_STATUSES = [
  { value: "acquistato", label: "Acquistato" },
  { value: "non_acquistato", label: "Non acquistato" },
  { value: "app_rimandato", label: "App. rimandato" },
  { value: "non_presentato", label: "Non presentato" },
  { value: "non_trattabile", label: "Non trattabile" },
];
export const consultationLabel = (v) => CONSULTATION_STATUSES.find((c) => c.value === v)?.label || "";
export const LEAD_STATUS_ORDER = Object.keys(LEAD_STATUS);

export const LEAD_DYNAMIC_FIELDS = [
  { id: "email", label: "Email" },
  { id: "city", label: "Città" },
  { id: "treatment", label: "Trattamento richiesto" },
  { id: "budget", label: "Budget" },
  { id: "preferred_time", label: "Orario preferito" },
  { id: "custom_q1", label: "Come ci hai conosciuto?" },
  { id: "custom_q2", label: "Disponibile a richiamata?" },
];

const pad2 = (n) => String(n).padStart(2, "0");

// ---------------------------------------------------------------- cache layer
let _leads = [];
let _hydrated = false;
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });

export const subscribeLeads = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const isLeadsReady = () => _hydrated;

export async function hydrateLeads() {
  try {
    const { data } = await api.get("/leads");
    _leads = Array.isArray(data) ? data : [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("hydrateLeads failed", e?.response?.status);
  } finally {
    _hydrated = true;
    notify();
  }
  return _leads;
}

export const resetLeads = () => { _leads = []; _hydrated = false; };
const resync = () => { hydrateLeads(); };

// ---------------------------------------------------------------- month helpers
export const monthKeyOf = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

export const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  const l = new Date(y, m - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return l.charAt(0).toUpperCase() + l.slice(1);
};

export const getMonthsForClient = (clientId) => {
  const set = new Set(_leads.filter((l) => l.client_id === clientId).map((l) => monthKeyOf(l.created_time)));
  return [...set].sort().map((key) => ({ key, label: monthLabel(key) }));
};

// ---------------------------------------------------------------- sync getters
export const getLeads = (clientId, monthKey) =>
  _leads
    .filter((l) => l.client_id === clientId && (!monthKey || monthKeyOf(l.created_time) === monthKey))
    .sort((a, b) => new Date(b.created_time) - new Date(a.created_time));

// ---------------------------------------------------------------- mutations
export const updateLead = (leadId, patch) => {
  const i = _leads.findIndex((l) => l.id === leadId || l.lead_id === leadId);
  if (i >= 0) {
    const next = { ..._leads[i], ...patch, updated_at: new Date().toISOString() };
    if (patch.lead_status) next.status = patch.lead_status;
    if (patch.status) next.lead_status = patch.status;
    _leads[i] = next;
    notify();
  }
  api.patch(`/leads/${leadId}`, patch).catch(resync);
};

// ---------------------------------------------------------------- KPIs
const _kpisFromLeads = (leads) => {
  const by = (s) => leads.filter((l) => l.lead_status === s).length;
  const generated = leads.length;
  const toManage = by("new") + by("to_contact");
  return {
    generated,
    managed: generated - toManage,
    toManage,
    appointments: by("appointment_booked"),
    noAnswer: by("no_answer_1") + by("no_answer_2"),
    noShow: by("no_show"),
    outsideArea: by("outside_service_area"),
    programSold: by("program_sold"),
  };
};

export const leadKpis = (clientId, monthKey) => _kpisFromLeads(getLeads(clientId, monthKey));

// Filtro per intervallo di date (created_time). from/to sono ISO datetime (to = fine giornata).
export const getLeadsRange = (clientId, from, to) =>
  _leads
    .filter((l) => l.client_id === clientId
      && (!from || l.created_time >= from)
      && (!to || l.created_time <= to))
    .sort((a, b) => new Date(b.created_time) - new Date(a.created_time));

export const leadKpisRange = (clientId, from, to) => _kpisFromLeads(getLeadsRange(clientId, from, to));

export const leadClient = (clientId) => clientById(clientId);
